import { Box, Flex } from '@chakra-ui/react';
import { Accordion, Tags, Text } from '@kula-ai/components';
import {
  GraduationHat01,
  Headphones01,
  Lock01,
  Mail01,
} from '@kula-ai/icons-core';
import type { JobInterviewStage, JobInterviewStepKind } from '../../../types';
import type {
  JobInterviewPlanTabProps,
  LockedStageRowProps,
  StepRowProps,
} from './JobInterviewPlanTab.types';

const STEP_ICON: Record<JobInterviewStepKind, typeof Headphones01> = {
  interview: Headphones01,
  assessment: GraduationHat01,
  email: Mail01,
};

const StepRow = ({ step }: StepRowProps) => {
  const Icon = STEP_ICON[step.kind];
  return (
    <Flex
      align="center"
      gap="8px"
      px="12px"
      py="8px"
      bg="secondary-50"
      borderRadius="6px"
    >
      <Box color="secondary-700">
        <Icon size={14} color="currentColor" />
      </Box>
      <Text variant="span" className="text-secondary-900">
        {step.name}
      </Text>
    </Flex>
  );
};

const LockedStageRow = ({ stage }: LockedStageRowProps) => (
  <Flex
    align="center"
    gap="8px"
    px="12px"
    py="10px"
    border="1px solid"
    borderColor="secondary-100"
    borderRadius="8px"
    color="secondary-400"
  >
    <Lock01 size={14} color="currentColor" />
    <Text variant="span" className="text-secondary-700">
      {stage.name}
    </Text>
  </Flex>
);

const buildAccordionItems = (stages: JobInterviewStage[]) =>
  stages
    .filter((stage) => !stage.locked)
    .map((stage) => ({
      value: stage.id,
      label: (
        <Flex align="center" justify="space-between" w="100%">
          <Text variant="span" className="text-secondary-900 font-medium">
            {stage.name}
          </Text>
          {stage.steps && stage.steps.length > 0 && (
            <Tags label={`${stage.steps.length} steps`} />
          )}
        </Flex>
      ),
      content: (
        <Flex direction="column" gap="6px" pt="6px">
          {(stage.steps ?? []).map((step) => (
            <StepRow key={step.id} step={step} />
          ))}
        </Flex>
      ),
    }));

export const JobInterviewPlanTab = ({ data }: JobInterviewPlanTabProps) => {
  const lockedStages = data.stages.filter((s) => s.locked);
  const items = buildAccordionItems(data.stages);

  return (
    <Flex direction="column" gap="6px">
      {lockedStages.map((stage) => (
        <LockedStageRow key={stage.id} stage={stage} />
      ))}
      {items.length > 0 && (
        <Accordion.Root
          type="multiple"
          items={items}
          defaultValue={items.map((i) => i.value)}
        />
      )}
    </Flex>
  );
};
